import { ScrollView, View, Text, StyleSheet } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useColors, ThemeColors } from '../hooks/useColors';

export default function TermsAndConditionsScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.title}>JadeTrade Terms and Conditions</Text>
        <Text style={styles.meta}>Last Updated: December 2025</Text>

        <Text style={styles.section}>1. Agreement to Terms</Text>
        <Text style={styles.paragraph}>
          These Terms and Conditions ("Terms") constitute a legally binding agreement between you 
          and JadeTrade Marketplace LLC ("JadeTrade," "we," "us," or "our") governing your access to 
          and use of the JadeTrade mobile application and related services (collectively, the 
          "Services").
        </Text>
        <Text style={styles.paragraph}>
          "You" refers to both buyers and sellers who access or use the Services.
        </Text>
        <Text style={styles.paragraph}>
          By accessing or using the Services, you confirm your agreement to be bound by these Terms. If you do not agree, do not use the Services.
        </Text>

        <Text style={styles.section}>2. Services Overview</Text>
        <Text style={styles.paragraph}>
          JadeTrade is a peer-to-peer marketplace connecting buyers with independent vendors for the 
          purchase of secondhand and personally sourced fashion and lifestyle products, with 
          integrated local delivery. The app provides:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Product browsing and purchasing
          </Text>
          <Text style={styles.listItem}>
            {"•"} Seller storefront management
          </Text>
          <Text style={styles.listItem}>
            {"•"} In-app messaging between buyers and sellers
          </Text>
          <Text style={styles.listItem}>
            {"•"} Real-time seller availability confirmation
          </Text>
          <Text style={styles.listItem}>
            {"•"} Integrated delivery via Uber Direct
          </Text>
          <Text style={styles.listItem}>
            {"•"} Payment processing via Stripe
          </Text>
          <Text style={styles.listItem}>
            {"•"} Order tracking and delivery notifications
          </Text>
        </View>

        <Text style={styles.section}>3. Account Requirements</Text>
        <Text style={styles.subSection}>3.1 Registration</Text>
        <Text style={styles.paragraph}>To use most features, you must create an account with:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} A valid email address
          </Text>
          <Text style={styles.listItem}>
            {"•"} Mobile phone number
          </Text>
          <Text style={styles.listItem}>
            {"•"} Full name
          </Text>
          <Text style={styles.listItem}>
            {"•"} Password
          </Text>
        </View>

        <Text style={styles.subSection}>3.2 Account Responsibility</Text>
        <Text style={styles.paragraph}>
          You are responsible for maintaining your account security and all activity under your 
          account. You agree to:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Provide accurate information
          </Text>
          <Text style={styles.listItem}>
            {"•"} Keep your information updated
          </Text>
          <Text style={styles.listItem}>
            {"•"} Notify us of any unauthorized access
          </Text>
          <Text style={styles.listItem}>
            {"•"} Log out after each session when using a shared device
          </Text>
        </View>

        <Text style={styles.section}>4. Seller Requirements</Text>
        <Text style={styles.paragraph}>
          If you choose to sell on JadeTrade, you agree to:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Accurately describe all items (including condition and dimensions)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Keep inventory up to date
          </Text>
          <Text style={styles.listItem}>
            {"•"} Confirm item availability promptly
          </Text>
          <Text style={styles.listItem}>
            {"•"} Package items securely for delivery
          </Text>
          <Text style={styles.listItem}>
            {"•"} Follow all local laws regarding the resale of goods and the collection of state or local fees
          </Text>
        </View>

        <Text style={styles.section}>5. Buyer Conduct</Text>
        <Text style={styles.paragraph}>As a buyer, you agree to:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Pay for all items purchased via your account
          </Text>
          <Text style={styles.listItem}>
            {"•"} Provide accurate delivery details
          </Text>
          <Text style={styles.listItem}>
            {"•"} Be available to receive deliveries or make proper arrangements
          </Text>
          <Text style={styles.listItem}>
            {"•"} Avoid abusing refund or cancellation policies
          </Text>
          <Text style={styles.listItem}>
            {"•"} Treat sellers and delivery personnel respectfully
          </Text>
        </View>

        <Text style={styles.section}>6. Ordering and Delivery</Text>
        <Text style={styles.subSection}>6.1 Seller Confirmation</Text>
        <Text style={styles.paragraph}>
          Before you can check out, the seller must confirm item availability. This prevents out-of-stock orders and improves delivery reliability.
        </Text>

        <Text style={styles.subSection}>6.2 Delivery Options</Text>
        <Text style={styles.paragraph}>
          JadeTrade uses Uber Direct to facilitate same-day delivery. By selecting delivery, you acknowledge:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Delivery is provided by Uber Direct, not JadeTrade
          </Text>
          <Text style={styles.listItem}>
            {"•"} Your order and contact details, including your delivery address, are shared with the seller and the delivery driver. Likewise, the seller's designated pickup location is shared with the buyer and the delivery driver for the purpose of completing the order and enabling real-time delivery tracking.
          </Text>
          <Text style={styles.listItem}>
            {"•"} Delivery times are estimates and not guaranteed
          </Text>
          <Text style={styles.listItem}>
            {"•"} Fees vary based on distance and delivery timing
          </Text>
          <Text style={styles.listItem}>
            {"•"} You must comply with Uber Direct's Terms of Service
          </Text>
        </View>

        <Text style={styles.section}>7. Payments and Fees</Text>
        <Text style={styles.subSection}>7.1 Payment Methods</Text>
        <Text style={styles.paragraph}>Accepted forms of payment:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Credit and debit cards via Stripe
          </Text>
        </View>

        <Text style={styles.subSection}>7.2 Fees</Text>
        <Text style={styles.paragraph}>Fees may include:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} A platform service fee
          </Text>
          <Text style={styles.listItem}>
            {"•"} A delivery fee (based on distance and order size)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Optional tips for delivery drivers
          </Text>
        </View>

        <Text style={styles.subSection}>7.3 Payouts to Sellers</Text>
        <Text style={styles.paragraph}>
          Sellers receive funds directly from Stripe after a successful transaction, minus applicable platform and delivery fees. To receive payouts, sellers must complete Stripe's onboarding and identity verification process. JadeTrade does not directly handle seller funds.
        </Text>

        <Text style={styles.subSection}>7.4 Taxes</Text>
        <Text style={styles.paragraph}>
          You are responsible for applicable taxes. JadeTrade may collect and remit taxes as required by law.
        </Text>

        <Text style={styles.section}>8. Intellectual Property</Text>
        <Text style={styles.subSection}>8.1 JadeTrade Content</Text>
        <Text style={styles.paragraph}>
          All content, branding, software, and designs provided by JadeTrade are the property of JadeTrade Marketplace LLC.
        </Text>

        <Text style={styles.subSection}>8.2 User Content</Text>
        <Text style={styles.paragraph}>
          You retain ownership of your submitted content (e.g., listings, photos, messages), but you grant us a limited license to use, display, and distribute it as necessary to operate the platform. You confirm you have rights to share all content you submit.
        </Text>

        <Text style={styles.section}>9. Prohibited Activities</Text>
        <Text style={styles.paragraph}>You agree not to:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Use the Services for unlawful purposes
          </Text>
          <Text style={styles.listItem}>
            {"•"} Sell or attempt to sell prohibited items including, but not limited to: alcohol, drugs, tobacco, nicotine products, controlled substances, pharmaceuticals, pornography, sexually explicit materials, live insects or animals, human remains or body parts, stolen goods, hazardous materials, and any other item prohibited under local, state, or federal law or Uber's General Terms of Use
          </Text>
          <Text style={styles.listItem}>
            {"•"} Create fake listings or misrepresent items
          </Text>
          <Text style={styles.listItem}>
            {"•"} Circumvent JadeTrade's payment or delivery systems
          </Text>
          <Text style={styles.listItem}>
            {"•"} Harass or threaten users or delivery drivers
          </Text>
          <Text style={styles.listItem}>
            {"•"} Create multiple accounts to avoid restrictions
          </Text>
          <Text style={styles.listItem}>
            {"•"} Scrape or misuse app data
          </Text>
          <Text style={styles.listItem}>
            {"•"} Interfere with app security or function
          </Text>
        </View>

        <Text style={styles.section}>10. Cancellations and Refunds</Text>
        <Text style={styles.subSection}>10.1 Order Cancellations</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Buyers may cancel orders before the seller confirms availability
          </Text>
          <Text style={styles.listItem}>
            {"•"} Orders are automatically cancelled and refunded if the seller does not respond within 10 minutes
          </Text>
          <Text style={styles.listItem}>
            {"•"} Cancellations after seller confirmation may not be possible if the item has been packed or picked up
          </Text>
        </View>

        <Text style={styles.subSection}>10.2 Automatic Refunds</Text>
        <Text style={styles.paragraph}>Full refunds are automatically issued if:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} The seller does not accept the order within 10 minutes
          </Text>
          <Text style={styles.listItem}>
            {"•"} The seller rejects the order
          </Text>
          <Text style={styles.listItem}>
            {"•"} Delivery cannot be completed due to service unavailability
          </Text>
          <Text style={styles.listItem}>
            {"•"} The buyer cancels before seller confirmation
          </Text>
        </View>

        <Text style={styles.subSection}>10.3 Refund Requests</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Refund requests for delivered orders must be submitted within 30 days
          </Text>
          <Text style={styles.listItem}>
            {"•"} Requests are reviewed on a case-by-case basis
          </Text>
          <Text style={styles.listItem}>
            {"•"} Approved refunds are issued to the original payment method
          </Text>
          <Text style={styles.listItem}>
            {"•"} Refunds typically process within 5–10 business days
          </Text>
        </View>

        <Text style={styles.subSection}>10.4 Delivery Issues</Text>
        <Text style={styles.paragraph}>
          Delivery-related issues may occur due to factors outside of JadeTrade's control, including courier handling, access limitations, or seller preparation.
        </Text>
        <Text style={styles.paragraph}>
          Refunds or credits are not guaranteed for delivery-related issues. In certain circumstances, JadeTrade may, at its sole discretion, review reports involving delivery problems such as non-delivery or order discrepancies and determine whether a resolution is appropriate.
        </Text>
        <Text style={styles.paragraph}>
          Delivery-related matters may also be subject to Uber Direct's policies and investigation process.
        </Text>

        <Text style={styles.section}>11. Dispute Resolution</Text>
        <Text style={styles.subSection}>11.1 Arbitration</Text>
        <Text style={styles.paragraph}>
          You agree to resolve disputes with JadeTrade through individual arbitration, excluding:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Small claims court
          </Text>
          <Text style={styles.listItem}>
            {"•"} Claims for physical injury
          </Text>
          <Text style={styles.listItem}>
            {"•"} Intellectual property injunctions
          </Text>
        </View>

        <Text style={styles.subSection}>11.2 No Class Actions</Text>
        <Text style={styles.paragraph}>
          You waive your right to participate in any class or representative actions.
        </Text>

        <Text style={styles.subSection}>11.3 Uber Direct Disputes</Text>
        <Text style={styles.paragraph}>
          Delivery-related disputes are subject to Uber Direct's policies and dispute resolution terms.
        </Text>

        <Text style={styles.subSection}>11.4 Buyer-Seller Disputes</Text>
        <Text style={styles.paragraph}>
          Disputes between a buyer and a seller should first be addressed through the in-app messaging and support tools. JadeTrade may assist but does not guarantee resolution.
        </Text>

        <Text style={styles.section}>12. Privacy and Data</Text>
        <Text style={styles.paragraph}>
          We collect account information, order data, delivery details, location data, and usage analytics to operate the Services.
        </Text>
        <Text style={styles.paragraph}>
          We share data with trusted service providers including:
        </Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Stripe (payment processing, seller onboarding, identity verification, and payouts)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Uber Direct (delivery fulfillment)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Supabase (data storage and authentication)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Google Maps (location services)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Twilio (SMS and phone verification)
          </Text>
          <Text style={styles.listItem}>
            {"•"} PostHog (analytics and session replay)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Sentry (error monitoring)
          </Text>
          <Text style={styles.listItem}>
            {"•"} Expo (push notification delivery)
          </Text>
        </View>
        <Text style={styles.paragraph}>
          For complete details on data practices, please review our Privacy Policy.
        </Text>

        <Text style={styles.section}>13. Disclaimers and Liability</Text>
        <Text style={styles.subSection}>13.1 Service Disclaimer</Text>
        <Text style={styles.paragraph}>
          We provide the Services "as is" without guarantees or warranties.
        </Text>

        <Text style={styles.subSection}>13.2 Vendor Content Disclaimer</Text>
        <Text style={styles.paragraph}>We do not guarantee:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Product quality or condition
          </Text>
          <Text style={styles.listItem}>
            {"•"} Accuracy of vendor listings
          </Text>
          <Text style={styles.listItem}>
            {"•"} Delivery timelines or availability
          </Text>
        </View>

        <Text style={styles.subSection}>13.3 Third-Party Disclaimer</Text>
        <Text style={styles.paragraph}>JadeTrade is not responsible for:</Text>
        <View style={styles.list}>
          <Text style={styles.listItem}>
            {"•"} Uber Direct delivery outcomes
          </Text>
          <Text style={styles.listItem}>
            {"•"} Stripe
          </Text>
          <Text style={styles.listItem}>
            {"•"} Misuse of your data by third parties
          </Text>
        </View>

        <Text style={styles.subSection}>13.4 Liability Cap</Text>
        <Text style={styles.paragraph}>
          Our liability is limited to the amount you paid to JadeTrade in the 12 months before any claim.
        </Text>

        <Text style={styles.section}>14. Termination</Text>
        <Text style={styles.subSection}>14.1 By You</Text>
        <Text style={styles.paragraph}>
          You may delete your account at any time in the app. All orders must be fulfilled first.
        </Text>

        <Text style={styles.subSection}>14.2 By JadeTrade</Text>
        <Text style={styles.paragraph}>
          We may suspend or terminate your account at any time and at our sole discretion for violating these Terms or for conduct that impacts the platform's integrity or safety.
        </Text>

        <Text style={styles.section}>15. Changes to Terms</Text>
        <Text style={styles.paragraph}>
          We may modify these Terms periodically. Updates will be posted in the app or on our website. Continued use of the Services indicates your agreement to updated Terms.
        </Text>

        <Text style={styles.section}>16. Force Majeure</Text>
        <Text style={styles.paragraph}>
          JadeTrade shall not be held liable for any delay or failure in performance of its obligations under these Terms due to events beyond its reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, labor disputes, internet or utility service interruptions, governmental actions, pandemics, or other unforeseen circumstances.
        </Text>

        <Text style={styles.section}>17. Governing Law</Text>
        <Text style={styles.paragraph}>
          These Terms shall be governed by and construed in accordance with the laws of the State of New York, without regard to conflict of law principles. Any legal action or proceeding shall be brought exclusively in the courts located in New York County, New York.
        </Text>

        <Text style={styles.section}>18. Contact Information</Text>
        <Text style={styles.paragraph}>
          Questions? Email us at: support@jadetradeapp.com
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  content: { padding: 16 },
  title: { fontSize: 24, fontWeight: "bold", marginBottom: 8, color: colors.text },
  meta: { fontSize: 12, color: colors.textLight, marginBottom: 16 },
  section: { fontSize: 18, fontWeight: "bold", marginTop: 12, marginBottom: 4, color: colors.text },
  subSection: {
    fontSize: 16,
    fontWeight: "600",
    marginTop: 8,
    marginBottom: 4,
    color: colors.text,
  },
  paragraph: { fontSize: 14, lineHeight: 20, marginBottom: 8, color: colors.text },
  list: { marginLeft: 16, marginBottom: 8 },
  listItem: { fontSize: 14, lineHeight: 20, marginBottom: 4, color: colors.text },
});
